import type {
	TemplatePhotoPosition,
	TemplatePosition,
	TemplateSet,
	TemplateTextPosition,
} from '@sosocio/creator-types/app';
import type * as DB from '@sosocio/creator-types/database';
import { resolveTemplatePositionSets } from '@sosocio/tools/template-position-sets';
import { ThemeDataModule } from 'store';
import { getAndParseTemplatePositionTextModelAttributes } from './project/text-objects';

/**
 * Builds fixed template variants from compatible photo and transformable text
 * positions. Non-transformable text positions are shared by every variant.
 *
 * @param templateId Identifier of the {@link DB.TemplateModel} to resolve.
 * @returns Fixed {@link TemplateSet} variants ordered by variant-position priority.
 */
export default function getFixedTemplateSets(templateId: DB.TemplateModel['id']): TemplateSet[] {
	const templatePositionStates = ThemeDataModule.templatePositionsStates.filter(
		(positionStateModel) => positionStateModel.templateid == templateId,
	);

	if (templatePositionStates.length === 0) {
		return [];
	}

	const resolvedSets = resolveTemplatePositionSets({
		photoPositions: ThemeDataModule.photopositions,
		states: templatePositionStates,
		textPositions: ThemeDataModule.textpositions,
	});

	return resolvedSets.map((resolvedSet): TemplateSet => {
		const positions = resolvedSet.positions.map((resolvedPosition): TemplatePhotoPosition | TemplateTextPosition => {
			const positionModel = resolvedPosition.position;
			const positionProps: TemplatePosition = {
				angle: positionModel.angle,
				available: resolvedPosition.available,
				bordercolor: positionModel.bordercolor,
				borderwidth: positionModel.borderwidth,
				height: positionModel.height,
				id: resolvedPosition.state.id,
				overlap_bottom: positionModel.overlap_bottom,
				overlap_left: positionModel.overlap_left,
				overlap_right: positionModel.overlap_right,
				overlap_top: positionModel.overlap_top,
				transformable: Boolean(positionModel.transformable),
				width: positionModel.width,
				x: positionModel.x_axis,
				y: positionModel.y_axis,
				z: positionModel.z_axis,
			};

			if (resolvedPosition.type === 'text') {
				const textPositionModel = positionModel as DB.TemplatePositionTextModel;

				return {
					...positionProps,
					align: textPositionModel.align,
					attributes: getAndParseTemplatePositionTextModelAttributes(textPositionModel),
					bgcolor: textPositionModel.bgcolor,
					fontbold: Boolean(textPositionModel.fontbold),
					fontcolor: textPositionModel.fontcolor,
					fontface: textPositionModel.fontface,
					fontitalic: Boolean(textPositionModel.fontitalic),
					pointsize: textPositionModel.pointsize,
					productattribute: textPositionModel.productattribute,
					snap: Boolean(textPositionModel.snap),
					styleable: Boolean(textPositionModel.styleable),
					type: 'text',
				};
			}

			const photoPositionModel = positionModel as DB.TemplatePositionPhotoModel;

			return {
				...positionProps,
				autorotate: Boolean(photoPositionModel.autorotate),
				fillMethod: photoPositionModel.fillMethod,
				flag: Boolean(photoPositionModel.flag),
				mask: photoPositionModel.mask,
				styleable: (
					typeof photoPositionModel.styleable === 'undefined'
						? true
						: Boolean(photoPositionModel.styleable)
				),
				type: 'photo',
			};
		});

		return {
			id: resolvedSet.id,
			positions,
			templateId,
		};
	});
}
